import { ButtonV2, TooltipV2 } from '@cimplico/components';
import { PencilSquareIcon, SwatchIcon, TagIcon, UserGroupIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { useRef, useState } from 'react';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { Worksheet, WorksheetStatusEnum, WorksheetTypeEnum } from '../../../types/worksheet';
import EditDetailsForm from './EditDetailsForm';
import Assigned from './Assigned';
import SidebarActions from './SidebarActions';
import StatusBadge from './StatusBadge';
import TopActions from './top-actions/TopActions';
import useIsClamped from '../../../hooks/use-line-clamp-active';
import QuickInfo from '../QuickInfo';
import XeroLogo from '../../../assets/xero.png';
import { formatDateTime } from '../../../utilities/formatting';
import { Link } from 'react-router-dom';
import { useFirmContext } from '../../../providers/FirmProvider';
type TopSectionProps = {
    worksheet: Worksheet;
    onXeroAuthError?: () => void;
};

function TopSection({ worksheet, onXeroAuthError }: TopSectionProps): JSX.Element {
    const { shortId } = useFirmContext();
    const workpaper = useWorkpaperContext();
    const canEdit = worksheet.type === WorksheetTypeEnum.GENERIC && !worksheet.account;
    const titleRef = useRef<HTMLHeadingElement | null>(null);
    const isClamped = useIsClamped(titleRef);

    return (
        <div>
            <div className="flex justify-between items-start">
                <section className="flex gap-2 min-w-[250px] flex-col w-full">
                    <TooltipV2 disabled={!isClamped} message={worksheet.name} position="bottom">
                        <h1 className="text-2xl font-bold line-clamp-2 break-all" ref={titleRef}>
                            {worksheet.name}
                            {canEdit && <EditDetails worksheet={worksheet} />}
                        </h1>
                    </TooltipV2>
                </section>
                <TopActions worksheet={worksheet} onXeroAuthError={onXeroAuthError} dataIntercomTarget="worksheet-ellipses-button" />
            </div>
            <p className="text-sm text-gray-600 max-w-[80ch]">{worksheet.description}</p>
            <div className="flex md:items-center justify-between flex-wrap flex-row gap-y-1 gap-x-4 mt-1">
                <div className="flex items-center shrink-0 flex-wrap gap-2">
                    <StatusBadge status={worksheet.status} />
                    <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                    {worksheet.account && (
                        <>
                            {worksheet.account.provider === 'XERO' && (
                                <>
                                    <TooltipV2
                                        disabled={!workpaper.trialBalanceRefreshedAt}
                                        message={'Last refreshed:' + formatDateTime(workpaper.trialBalanceRefreshedAt || '')}
                                        delayMs={750}
                                    >
                                        <img src={XeroLogo} className="h-6 w-6 shrink-0" />
                                    </TooltipV2>
                                    <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                                </>
                            )}
                            <QuickInfo label={worksheet.account.type} icon={<SwatchIcon />} />
                            <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                        </>
                    )}
                    {worksheet.tag && (
                        <>
                            <QuickInfo label={worksheet.tag} icon={<TagIcon />} />
                            <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                        </>
                    )}

                    <div className="flex gap-2">
                        <UserGroupIcon className="text-gray-500 h-5 w-5 inline" />
                        <Assigned worksheet={worksheet} />
                    </div>
                    {worksheet.priorWorksheetId && workpaper.priorWorkpaperId && (
                        <>
                            <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                            <Link
                                to={`/${shortId}/workpapers/${workpaper.priorWorkpaperId}/${
                                    worksheet.account ? 'trial-balance' : 'other-schedules'
                                }/${worksheet.priorWorksheetId}`}
                                className="text-sm text-primary flex items-center gap-1"
                                target="_blank"
                            >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" /> Open prior worksheet
                            </Link>
                        </>
                    )}
                </div>
                <SidebarActions worksheet={worksheet} />
            </div>
        </div>
    );
}

function EditDetails({ worksheet }: TopSectionProps): JSX.Element {
    const [showEdit, setShowEdit] = useState(false);
    const { readOnly } = useWorkpaperContext();
    const isExcluded = worksheet.status === WorksheetStatusEnum.EXCLUDED;

    return (
        <>
            {showEdit && <EditDetailsForm worksheet={worksheet} onClose={(): void => setShowEdit(false)} />}
            <ButtonV2
                type="hollow"
                leftIcon={<PencilSquareIcon className="text-gray-500" />}
                isDisabled={readOnly || isExcluded}
                disabledHint={isExcluded ? 'Excluded worksheet cannot be updated.' : undefined}
                onClick={(): void => setShowEdit(true)}
            />
        </>
    );
}

export default TopSection;
